import { pieceKind, Player } from "../types/game";

export const groupBy = <T, K extends keyof any>(
  list: T[],
  getKey: (item: T) => K
) =>
  list.reduce((previous, currentItem) => {
    const group = getKey(currentItem);
    if (!previous[group]) previous[group] = [];
    previous[group].push(currentItem);
    return previous;
  }, {} as Record<K, T[]>);

export class Hex {
  q: number;
  r: number;

  constructor(q: number, r: number) {
    this.q = q;
    this.r = r;
  }

  static unitVectors = [
    new Hex(1, 0),
    new Hex(1, -1),
    new Hex(0, -1),
    new Hex(-1, 0),
    new Hex(-1, 1),
    new Hex(0, 1),
  ];

  valueOf = () => `${this.q},${this.r}`;

  add = (hex: Hex) => new Hex(this.q + hex.q, this.r + hex.r);

  rotate60Left = () => new Hex(this.q + this.r, -this.q);
  rotate60Right = () => new Hex(-this.r, this.q + this.r);

  neighbours = (): Hex[] => Hex.unitVectors.map((u) => u.add(this));

  distanceTo = (hex: Hex) => {
    return Math.max(
      Math.abs(this.q - hex.q),
      Math.abs(this.r - hex.r),
      Math.abs(-(this.q + this.r) + (hex.q + hex.r))
    );
  };
}

export class ReservePiece {
  kind: pieceKind;
  owner: Player;

  constructor(kind: pieceKind, owner: Player) {
    this.kind = kind;
    this.owner = owner;
  }
}

export class Piece extends ReservePiece {
  hex: Hex;
  h: number;

  constructor(kind: pieceKind, owner: Player, hex: Hex, h: number) {
    super(kind, owner);
    this.hex = hex;
    this.h = h;
  }

  neighbourHex = () => this.hex.neighbours();

  copy = () => new Piece(this.kind, this.owner, this.hex, this.h);

  valueOf = () => `${this.hex.q},${this.hex.r},${this.h}`;
}

export class Board {
  pieces: Piece[];

  constructor(pieces: Piece[]) {
    this.pieces = pieces;
  }

  copy = () => new Board(this.pieces.map((p) => p.copy()));

  copyWithout = (piece: Piece) =>
    new Board(this.pieces.filter((p) => p.valueOf() !== piece.valueOf()));

  // Find out if a hex is occupied or not
  isFree = (hex: Hex) =>
    !this.pieces.some((p) => p.hex.valueOf() === hex.valueOf());

  // Get all neighbouring pieces to a hex
  neighbours = (hex: Hex) => {
    const neighbours = this.pieces.filter((p) => hex.distanceTo(p.hex) === 1);

    const groups = groupBy<Piece, string>(neighbours, (p) => p.hex.valueOf());

    return Object.values(groups).map((g) =>
      g.reduce((highest, curr) => (curr.h > highest.h ? curr : highest))
    );
  };

  hasNeighbour = (hex: Hex) => this.neighbours(hex).length;

  isOnTopOfBoard = (piece: Piece) =>
    !this.pieces.some(
      (p) => p.hex.valueOf() === piece.hex.valueOf() && p.h > piece.h
    );

  // Find all legal hexes for the player to place a new piece on
  legalPlacements = (playerId: number) => {
    const spots: Hex[] = [];
    this.pieces.forEach((p) => {
      p.neighbourHex().forEach((n) => {
        const isNotAlreadyInSpotsList = !spots.some(
          (s) => s.valueOf() === n.valueOf()
        );

        if (this.isFree(n) && isNotAlreadyInSpotsList) {
          spots.push(n);
        }
      });
    });

    // All free spots are legal if player doesn't have pieces placed
    if (!this.pieces.some((p) => p.owner.id === playerId)) {
      return spots;
    }

    // Remove all spots touching other player's pieces
    return spots.filter((s) => {
      return !this.neighbours(s).some((n) => n.owner.id !== playerId);
    });
  };

  // Returns true if hive is strongly connected
  static isCompact = (board: Board): boolean => {
    if (board.pieces.length <= 1) {
      return true;
    }

    const graph = new Board(
      board.pieces.filter(
        (p) => p.h === 0 // not interested in height here
      )
    );

    const visitedNodes: Piece[] = [];

    let adjacentNodes = [graph.pieces[0]];

    // Consume all proximal nodes
    while (adjacentNodes.length) {
      const currentNode = adjacentNodes.shift() as Piece;
      visitedNodes.push(currentNode);
      // Add to adjacent nodes all unvisited neighbours of the current node
      adjacentNodes = distinct([
        ...adjacentNodes,
        ...graph
          .neighbours(currentNode.hex)
          .filter(
            (n) => !visitedNodes.some((v) => v.valueOf() === n.valueOf())
          ),
      ]);
    }

    return visitedNodes.length === graph.pieces.length;
  };

  // Returns true if hive is still strongly connected without the given piece
  hiveStaysCompact = (withoutPiece: Piece): boolean =>
    Board.isCompact(this.copyWithout(withoutPiece));

  // Check if a piece can be moved
  canBeMoved = (piece: Piece) => {
    const canBe =
      this.isOnTopOfBoard(piece) &&
      this.hiveStaysCompact(piece) &&
      !!this.legalMovements(piece).length;

    return canBe;
  };

  // Returns a new board with the given piece moved
  boardWithPieceMoved = (piece: Piece, to: Hex) =>
    this.copy().movePiece(piece, to);

  // Find the first available spot after a jump, returns null if the jump is not possible
  findJumpLanding = (hex: Hex, unitVector: Hex) => {
    const nextHex = hex.add(unitVector);
    if (this.isFree(nextHex)) {
      return null;
    }

    let jumpLanding = nextHex.add(unitVector);

    while (!this.isFree(jumpLanding)) {
      jumpLanding = jumpLanding.add(unitVector);
    }

    return jumpLanding;
  };

  reachableNeighboursSliding = (hex: Hex): Hex[] =>
    Hex.unitVectors
      .map((u) => {
        const newHex = hex.add(u);

        const isFree = this.isFree(newHex);
        const isCloselySliding = this.neighbours(hex).some((nPrev) =>
          this.neighbours(newHex).some(
            (nNext) => nPrev.valueOf() === nNext.valueOf()
          )
        );
        const hasEnoughWidth =
          this.isFree(hex.add(u.rotate60Left())) ||
          this.isFree(hex.add(u.rotate60Right()));

        if (isFree && isCloselySliding && hasEnoughWidth) {
          return newHex;
        }
        return null;
      })
      .filter((h) => !!h) as Hex[]; // maybe type is wrong

  getMovesOnSide = (piece: Piece): Hex[] => {
    if (this.pieces.length === 0) {
      return [];
    }

    const visited: Hex[] = [];

    let reachable = this.reachableNeighboursSliding(piece.hex);

    // Consume all proximal hexes
    while (reachable.length) {
      const currentHex = reachable.shift() as Hex;
      visited.push(currentHex);

      reachable = distinct([
        ...reachable,
        ...this.reachableNeighboursSliding(currentHex).filter(
          (n) =>
            n.valueOf() !== piece.hex.valueOf() &&
            !visited.some((v) => v.valueOf() === n.valueOf())
        ),
      ]);
    }

    // Don't forget to allow filtering
    // Don't forget to deduplicate moves
    return distinct(visited); // .filter((v) => v.valueOf() !== piece.hex.valueOf());
  };

  getMovesOnSideFixed = (piece: Piece, distance: number): Hex[] => {
    if (this.pieces.length === 0) {
      return [];
    }

    const paths: Hex[][] = [[piece.hex]];

    for (let d = 1; d <= distance + 1; d++) {
      const longerPath: Hex[][] = [];

      // for each path
      // determine travel without going back
      // push new, longer path
      paths
        .filter((p) => p.length === d)
        .forEach((path) => {
          // Possible sliding moves without going back
          const nextMoves = this.reachableNeighboursSliding(
            path[path.length - 1]
          ).filter((n) => !path.some((h) => h.valueOf() === n.valueOf()));

          nextMoves.forEach((move) => {
            longerPath.push([...path, move]);
          });
        });
      if (longerPath.length) {
        paths.push(...longerPath);
      }
    }

    return paths
      .filter((p) => p.length === distance + 1)
      .map((p) => p[p.length - 1]);
  };

  // Find all legal movements for a piece
  legalMovements = (piece: Piece) => {
    switch (piece.kind) {
      case "queen":
        return this.copyWithout(piece).getMovesOnSideFixed(piece, 1);

      case "ant":
        return this.copyWithout(piece).getMovesOnSide(piece);

      case "beetle":
        return piece.hex.neighbours().filter((h) => {
          const board = this.copyWithout(piece);
          return board.hasNeighbour(h) || !board.isFree(h);
        });

      case "grasshopper":
        return Hex.unitVectors
          .map((u) => this.findJumpLanding(piece.hex, u))
          .filter((h) => h !== null) as Hex[];

      case "spider":
        return this.copyWithout(piece).getMovesOnSideFixed(piece, 3);

      default:
        return [];
    }
  };

  // Get the height of the highest piece on a given hex
  getHeightAt = (hex: Hex) => {
    const piecesAtHex = this.pieces.filter(
      (p) => p.hex.valueOf() === hex.valueOf()
    );
    return piecesAtHex.length
      ? piecesAtHex.reduce((highest, curr) =>
          curr.h > highest.h ? curr : highest
        ).h
      : -1;
  };

  // Place a new piece
  placePiece = (pieceKind: pieceKind, owner: Player, hex: Hex) => {
    const h = this.getHeightAt(hex);
    this.pieces.push(new Piece(pieceKind, owner, hex, h + 1));
  };

  // Move a piece
  movePiece = (piece: Piece, to: Hex) => {
    const toMove = this.pieces.find((p) => p.valueOf() === piece.valueOf());

    if (!toMove) {
      throw new Error("unable to find piece to move in board");
    }
    const newHeight = this.getHeightAt(to) + 1;
    toMove.h = newHeight;
    console.log("h+1", newHeight);
    console.log("NEW HEIGHT:", toMove.h);
    toMove.hex = to;
    return this;
  };

  checkIfPlayerLost = () => {
    const queens = this.pieces.filter((p) => p.kind === "queen");
    const players = Array.from(new Set(queens.map((q) => q.owner)));
    return players
      .map((p) => {
        if (
          queens
            .filter((q) => q.owner.id === p.id)
            .some((q) => !q.neighbourHex().some((n) => this.isFree(n)))
        ) {
          return p;
        }
        return undefined;
      })
      .filter((a) => !!a) as Player[];
  };
}

const distinct = <T extends { valueOf: () => string }>(pieces: T[]): T[] => [
  ...new Map(pieces.map((p) => [p.valueOf(), p])).values(),
];
